import React, { useState, useEffect, useRef, useCallback } from "react";
import PaginationSection from "../../components/Product/Pagination";
import { FAQ_TAB_LIST_URL, FAQ_LIST_URL } from '../../utils/urls';
import instance from '../../services/services';
import { useLanguage } from "../../hooks/useLanguage";

function FaqList(){
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('all');
  const [faqTabList, setFaqTabList] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null); // 열린 FAQ 항목의 인덱스
  const dropDownRefs = useRef([]);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1280);
  const [currentBoardType, setCurrentBoardType] = useState(""); // 현재 선택된 board_type

  const [faqList, setFaqList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 12; // 페이지당 표시할 항목 수

    const faqTabKeyMap = {
        "전체": "faq_tab_all",
        "장비대여": "faq_tab_rental",
        "장비반납": "faq_tab_return",
        "파손/분실": "faq_tab_damage_loss",
        "결제관련": "faq_tab_payment",
        "회원정보": "faq_tab_member",
        "기타": "faq_tab_etc"
    };

  // 자주하는질문 탭 api
  useEffect(() => {
    const fetchFaqTab = async () => {
      try {
        const response = await instance.post(FAQ_TAB_LIST_URL);
        setFaqTabList(response.data.data);

      } catch (error) {
        console.error("FAQ 탭 목록 가져오기실패:", error);
      }
    };
    fetchFaqTab();
  }, []);

  // 자주하는질문 리스트 api
  useEffect(() => {
    const fetchFaqList = async () => {
      try {
        const faqResponse = await instance.post(FAQ_LIST_URL, {
          boardid: "faq",
          offset: currentPage, // 현재 페이지 번호
          board_type: currentBoardType, // 선택된 탭의 board_type
          searchText: "",
          limit: itemsPerPage
        });
        setFaqList(faqResponse.data.data.data);
        setTotalCount(faqResponse.data.data.cntRow || 0); // 전체 항목 수

      } catch (error) {
        console.error("FAQ 목록 가져오기 실패:", error);
      }
    };
    fetchFaqList();
  }, [currentBoardType, currentPage]); // board_type이나 페이지가 변경될 때마다 다시 호출


  // 드롭다운 높이 설정
  const setDropdownHeight = useCallback(() => {
    dropDownRefs.current.forEach(item => {
      if (item) {
        item.style.height = isTablet ? '76px' : '56px';
      }
    });
  }, [isTablet]);

  // 반응형 체크
  useEffect(() => {
    const handleResize = () => {
      setIsTablet(window.innerWidth <= 1280);
      setDropdownHeight();
    };

    handleResize(); // 초기 실행
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setDropdownHeight]);

  // 드롭다운 열기/닫기
  const handleFaqClick = (idx) => {
    setActiveIndex(prevIdx => prevIdx === idx ? null : idx);

    dropDownRefs.current.forEach((item, index) => {
      if (!item) return;

      if (index === idx && idx === activeIndex) {
        // 열린 상태에서 다시 클릭하면 닫기
        item.style.height = isTablet ? '76px' : '56px';
      } else if (index === idx) {
        // 클릭한 항목 열기
        const contentHeight = item.lastElementChild.offsetHeight;
        const itemHeight = isTablet ? 76 : 56;
        item.style.height = `${contentHeight + itemHeight}px`;
      } else {
        // 다른 항목들 닫기
        item.style.height = isTablet ? '76px' : '56px';
      }
    });
  };

  // 페이지 변경 핸들러
  const handlePageChange = (page) => {
    setCurrentPage(page);
    setActiveIndex(null); // 페이지 변경 시 열린 FAQ 초기화
    setDropdownHeight(); // 페이지 변경 시 높이 초기화
    // window.scrollTo(0, 0); // 페이지 상단으로 스크롤
  };

  // 화살표 클릭 핸들러
  const handleArrowClick = (page) => {
    handlePageChange(page);
  };

  // 탭 클릭 핸들러
  const handleTabClick = (value) => {
    setActiveTab(value);
    setCurrentPage(1); // 탭 변경 시 페이지 1로 초기화
    setActiveIndex(null); // 탭 변경 시 열린 FAQ 초기화
    setDropdownHeight(); // 탭 변경 시 높이 초기화

    // 전체 탭을 클릭한 경우 board_type을 빈 문자열로 설정
    if (value === 'all') {
      setCurrentBoardType("");
    } else {
      // 다른 탭을 클릭한 경우 해당 탭의 codecd를 board_type으로 설정
      const selectedTab = faqTabList.find(tab => tab.value === value);
      if (selectedTab) {
        setCurrentBoardType(selectedTab.codecd);
      }
    }
  };

  return(
    <div className="board_container">
      <h3 className="commu_title noline">{t("frequently_asked_questions")}</h3>

      <div className="round_tab line">

        <ul className="tab_list">
            <li
              className={`tab_item ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => handleTabClick('all')}
            >
              <button className="flex-center-4 flex-justify-center">
                {t("faq_tab_all")}
              </button>
            </li>

            {faqTabList.map((tab, index) => (
              <li
                key={index}
                className={`tab_item ${activeTab === tab.value ? 'active' : ''}`}
                onClick={() => handleTabClick(tab.value)}
              >
                <button className="flex-center-4 flex-justify-center">
                    {t(faqTabKeyMap[tab.value] || tab.value)}
                </button>
              </li>
            ))}
        </ul>
      </div>


      <ul className="board_list faq">
        {faqList.map((faq, index) => (
          <li
            key={index}
            ref={el => dropDownRefs.current[index] = el}
            className={`board_item ${activeIndex === index ? 'active' : ''}`}
            onClick={() => handleFaqClick(index)}
          >
            <div className="title w-100">
              <i className="question_mark" />
              <span className="board_ctg">{t(faqTabKeyMap[faq.board_type_name] || faq.board_type_name)}</span>
              <span className="text_title ellipsis1">
                {faq.subject}
              </span>
            </div>

            <div className="title answer w-100">
              <i className="question_answer" />
              <div className={`summary_content ${activeIndex === index ? 'show' : ''}`}
              style={{paddingLeft: '0px'}}>

                <div dangerouslySetInnerHTML={{ __html: faq?.contents }}></div>
            </div>
            </div>
          </li>
        ))}
      </ul>

      {totalCount > 0 && (
        <PaginationSection
          totalCount={totalCount}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onArrow={handleArrowClick}
        />
      )}
    </div>
  )
};

export default FaqList;
